import { ChevronLeftIcon, ChevronRightIcon, XIcon } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from '@voll-intelligence/ui-kit/ui/dialog';
import { Separator } from '@voll-intelligence/ui-kit/ui/separator';
import { cn } from '@voll-intelligence/ui-kit/utils';
import type { HotelRoomsContextView, HotelRoomView } from '../models.js';
import type { HotelRoomsCopy } from './hotel_rooms.js';
import { HotelPhotoPlaceholder, useLivePhotoIndex, useLivePhotos } from '../live_photos.js';
import { HotelRoomFacts } from './hotel_room_group.js';

/** Controle que flutua sobre a foto — as duas setas e o X usam o mesmo: `glass` é
 *  o token de objeto sobre conteúdo. Uma regra pra controle em cima de imagem,
 *  não três. O raio é o do `Button` (`icon-sm` = `rounded-md`): botão só-ícone
 *  nunca é circular fora de pílula (#2530); só o contador, que É uma pílula. */
const OVER_PHOTO = 'glass';

/** Galeria do topo: a foto sangra a largura do modal e os controles flutuam sobre
 *  ela. O contador diz quantas FALTAM, que é o que decide se vale continuar.
 *
 *  O protótipo usa o carrossel do ui-kit (embla, com arraste); aqui a navegação
 *  segue por índice, como já era. Embla mede o DOM, e o modal roda em jsdom nos
 *  testes — arraste não vale perder a cobertura da navegação.
 *
 *  Contador e setas contam só as fotos que carregam (#4359): todas as `img` ficam
 *  no DOM e só a corrente aparece, então as outras baixam ao abrir e a que falha
 *  sai da conta antes de a pessoa chegar nela — sem isto o "1/8" só encolheria
 *  quadro a quadro, com telas quebradas no caminho. São no máximo oito fotos de
 *  um modal que a pessoa abriu de propósito. Nenhuma viva: o lugar da foto vira
 *  o mesmo placeholder do card do quarto. */
function HotelRoomGallery({
  room,
  copy,
}: {
  readonly room: HotelRoomView;
  readonly copy: HotelRoomsCopy;
}) {
  const { live, fail } = useLivePhotos(room.photos);
  const [current, select] = useLivePhotoIndex(live);

  if (room.photos.length === 0) return null;
  if (live.length === 0) return <HotelPhotoPlaceholder className="aspect-[4/3] w-full shrink-0" />;
  const total = live.length;
  const step = (delta: number) => {
    select((current + delta + total) % total);
  };

  /* Fora de cena vão o ATRIBUTO `hidden` (pela árvore de acessibilidade) E a
     utilitária `hidden` (#5617): o Preflight escopado do ui-kit declara
     `.genui2 :where(img) { display: block }` e não traz o `[hidden]` do global —
     regra de autor vence a folha do agente, e as fotos empilhavam no modal. */
  return (
    <div className="relative shrink-0">
      {live.map((photo, index) => (
        <img
          key={photo.id}
          className={cn('aspect-[4/3] w-full bg-muted object-cover', index !== current && 'hidden')}
          src={photo.url}
          alt={photo.alt}
          hidden={index !== current}
          onError={() => {
            fail(photo.url);
          }}
        />
      ))}
      {total > 1 && (
        <>
          <Button
            variant="outline"
            size="icon-sm"
            aria-label={copy.previousPhotoLabel}
            className={`absolute inset-y-0 left-3 my-auto ${OVER_PHOTO}`}
            onClick={() => {
              step(-1);
            }}
          >
            <ChevronLeftIcon />
          </Button>
          <Button
            variant="outline"
            size="icon-sm"
            aria-label={copy.nextPhotoLabel}
            className={`absolute inset-y-0 right-3 my-auto ${OVER_PHOTO}`}
            onClick={() => {
              step(1);
            }}
          >
            <ChevronRightIcon />
          </Button>
          <span
            className={`absolute right-3 bottom-3 rounded-full px-2 py-0.5 text-xs tabular-nums ${OVER_PHOTO}`}
          >
            {current + 1}/{total}
          </span>
        </>
      )}
    </div>
  );
}

/** Seção vazia é pior que seção ausente: o rótulo prometeria uma lista que não
 *  vem. Sem glifo por item porque a comodidade do quarto chega como texto solto
 *  do fornecedor — o ícone volta quando ela tiver código de catálogo. */
function HotelRoomDetailAmenities({
  room,
  copy,
}: {
  readonly room: HotelRoomView;
  readonly copy: HotelRoomsCopy;
}) {
  if (room.amenities.length === 0) return null;
  return (
    <>
      <Separator />
      <section className="grid gap-3">
        <h3 className="text-xs font-semibold tracking-wider text-muted-foreground uppercase">
          {copy.roomAmenitiesLabel}
        </h3>
        <ul className="grid grid-cols-2 gap-x-3 gap-y-2.5 text-sm">
          {room.amenities.map((amenity) => (
            <li key={amenity} className="min-w-0 truncate">
              {amenity}
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}

export function HotelRoomDetailModal({
  room,
  copy,
  context,
  onClose,
}: {
  readonly room: HotelRoomView;
  readonly copy: HotelRoomsCopy;
  readonly context?: HotelRoomsContextView;
  readonly onClose: () => void;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const openerRef = useRef<HTMLElement | null>(
    typeof document !== 'undefined' && document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null,
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      onClose();
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [onClose]);

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      {/* `80dvh` + `minmax(0,1fr)`: o modal para de crescer e a régua inteira
          (foto + texto) rola por dentro. `overflow-hidden` é o que faz a foto
          respeitar o raio do canto de cima. */}
      <DialogContent
        className="grid max-h-[80dvh] w-full grid-rows-[minmax(0,1fr)] gap-0 overflow-hidden p-0"
        size="md"
        showCloseButton={false}
        aria-label={room.name}
        initialFocus={closeRef}
        finalFocus={openerRef}
      >
        {/* O X de fábrica é `ghost` e cai em cima da foto, onde some. O nosso é o
            mesmo controle das setas. */}
        <DialogClose
          render={
            <Button
              ref={closeRef}
              variant="outline"
              size="icon-sm"
              aria-label={copy.closeLabel}
              className={`absolute top-3 right-3 z-10 ${OVER_PHOTO}`}
            />
          }
        >
          <XIcon />
        </DialogClose>

        <div className="scrollbar-rail [scrollbar-gutter:auto]! grid min-h-0 content-start overflow-y-auto">
          <HotelRoomGallery room={room} copy={copy} />
          <div className="grid gap-4 p-4">
            <div className="grid gap-0.5">
              <DialogTitle className="text-base font-medium">{room.name}</DialogTitle>
              {/* Apoio, não título: quem abriu o modal já sabe em que hotel está. */}
              {context?.hotelName && (
                <p className="text-sm text-muted-foreground">{context.hotelName}</p>
              )}
            </div>
            <HotelRoomFacts room={room} />
            {room.about && <p className="text-sm whitespace-pre-line">{room.about}</p>}
            <HotelRoomDetailAmenities room={room} copy={copy} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
